import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { DollarSign, Plus, Pencil } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import type { NurseryPriceList } from '../../types/nursery';

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-neutral-100 text-neutral-600',
  active: 'bg-green-50 text-green-700 border border-green-100',
  archived: 'bg-amber-50 text-amber-700 border border-amber-100',
};

/**
 * /accounts/price-lists. A customer is priced from one list: the one chosen
 * on its Prices tab, else its head office's, else the default marked here.
 */
function PriceListList() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  // An accounts seat, or a Retail manager: a shop sets its own prices.
  const accounts = useModuleLicense('accounts', ['user', 'admin']);
  const shop = useModuleLicense('retail', 'manager');
  const canManage = accounts.hasAccess || shop.hasAccess;
  const [lists, setLists] = useState<NurseryPriceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settingDefault, setSettingDefault] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await nurseryService.getPriceLists();
      if (res.success) setLists(res.data || []);
      else setError(errorMessage(res, 'Failed to load price lists'));
    } catch (e) {
      setError(errorMessage(e, 'Failed to load price lists'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const makeDefault = async (id: number) => {
    setSettingDefault(id);
    try {
      const res = await nurseryService.setDefaultPriceList(id);
      if (!res.success) setError(errorMessage(res, 'Could not make that list the default'));
      else await load();
    } catch (e) {
      setError(errorMessage(e, 'Could not make that list the default'));
    } finally {
      setSettingDefault(null);
    }
  };

  const hasDefault = lists.some(pl => pl.is_default);

  return (
    <>
      <PageHeader
        title="Price Lists"
        subtitle="Each customer is priced from one list; the default covers everyone not given another"
        icon={<DollarSign className="h-6 w-6 text-white" />}
        actionButtons={
          canManage && (
            <Button variant="primary" size="sm" onClick={() => navigate('/accounts/price-lists/new')}>
              <Plus className="h-4 w-4" /> New Price List
            </Button>
          )
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
        )}

        {!loading && lists.length > 0 && !hasDefault && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            No list is the default, so a customer with no list chosen gets no prices. Choose one below.
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : lists.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No price lists yet</p>
            <p className="text-sm mt-1">Create a price list, then make it the default.</p>
            {canManage && (
              <Button variant="primary" className="mt-4" onClick={() => navigate('/accounts/price-lists/new')}>
                <Plus className="h-4 w-4" /> New Price List
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
            {lists.map(pl => (
              <div key={pl.id} className="flex items-start gap-3 px-4 py-3 hover:bg-neutral-50">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium text-neutral-800 break-words min-w-0">{pl.name}</span>
                    {pl.is_default && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-primary-50 text-primary-700 border border-primary-100">Default</span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_STYLES[pl.status] || ''}`}>{pl.status}</span>
                  </div>
                  <p className="text-xs text-neutral-500 break-words">
                    {formatLocalDateInTz(pl.effective_from, orgTz)}
                    {pl.effective_to ? ` – ${formatLocalDateInTz(pl.effective_to, orgTz)}` : ' – ongoing'}
                    {` · ${pl.item_count ?? 0} prices`}
                    {pl.is_default
                      ? ' · every customer not given another list'
                      : ` · ${pl.customer_count ?? 0} customer${Number(pl.customer_count ?? 0) === 1 ? '' : 's'}`}
                  </p>
                  {canManage && !pl.is_default && pl.status !== 'archived' && (
                    <button
                      type="button"
                      onClick={() => makeDefault(pl.id)}
                      disabled={settingDefault !== null}
                      className="mt-1 text-xs font-medium text-primary-600 hover:underline disabled:opacity-50"
                    >
                      {settingDefault === pl.id ? 'Making default…' : 'Make default'}
                    </button>
                  )}
                </div>
                {canManage && (
                  <button
                    onClick={() => navigate(`/accounts/price-lists/${pl.id}/edit`)}
                    className="text-neutral-400 hover:text-primary-600 p-1"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default PriceListList;
